// flickscroller.ts ↔ game/flickscroller.cpp — momentum scrolling off the pen
// flick vector (the two-finger flick map scroll). PRESENTATION float: gesture
// chrome over the wall clock, never sim-affecting. The C++ globals are
// injected: gevm → evm (GetFlickVector reads the pen history), and
// HostGetMillisecondCount → clockMs.

import type { EventMgr, FlickVector } from './event.ts';

// ht.h:1885
const kcmsFlickQuantum = 150;

// ht.h:1876-1883 — FlickVector::GetMagnitude
function GetMagnitude(fliv: FlickVector): number {
  return fliv.dx * fliv.dx + fliv.dy * fliv.dy;
}

// ht.h:2585-2606 + game/flickscroller.cpp
export class FlickScroller {
  private m_fliv: FlickVector = { dx: 0, dy: 0, cms: 0 };
  private m_msStart = 0;
  private m_flMultiplier = 0; // stored but unused by the shipped GetPosition arm
  private m_flDecayPercent = 0;
  private m_cmsDecaySpan = 0; // dword (the float Init param truncates on store)
  private m_fHasMagnitude: boolean;

  private readonly evm: EventMgr;
  private readonly clockMs: () => number;

  // game/flickscroller.cpp:6-9
  constructor(evm: EventMgr, clockMs: () => number) {
    this.evm = evm;
    this.clockMs = clockMs;
    this.m_fHasMagnitude = false;
  }

  // game/flickscroller.cpp:11-36 (defaults: ht.h:2590-2592)
  Init(
    nPen: number,
    flMultiplier = 1.0,
    flDecayPercent = 0.01,
    cmsDecaySpan: number = kcmsFlickQuantum / 10,
    fChoose = true,
  ): boolean {
    // Get the best flick vector.

    if (!fChoose) {
      this.evm.GetFlickVector(nPen, this.m_fliv);
    } else {
      const flivPreferred: FlickVector = { dx: 0, dy: 0, cms: 0 };
      const flivAlternate: FlickVector = { dx: 0, dy: 0, cms: 0 };
      this.evm.GetFlickVector(nPen, flivPreferred);
      this.evm.GetFlickVector(nPen ^ 3, flivAlternate);
      if (GetMagnitude(flivAlternate) > GetMagnitude(flivPreferred)) {
        this.m_fliv = flivAlternate;
      } else {
        this.m_fliv = flivPreferred;
      }
    }

    this.m_msStart = this.clockMs();
    this.m_flMultiplier = flMultiplier;
    this.m_flDecayPercent = flDecayPercent;
    this.m_cmsDecaySpan = Math.trunc(cmsDecaySpan) >>> 0; // float -> dword member
    return this.CheckMagnitude(this.m_fliv.dx, this.m_fliv.dy);
  }

  // game/flickscroller.cpp:38-42
  Clear(): void {
    this.m_fHasMagnitude = false;
  }

  // game/flickscroller.cpp:44-92 — the shipped #else arm (the #if 0 arm decayed
  // per m_fliv.cms quantum instead of per m_cmsDecaySpan; dead code, not ported)
  GetPosition(ppt: { x: number; y: number }): boolean {
    if (!this.m_fHasMagnitude) return false;

    let dx = 0.0;
    let dy = 0.0;

    let flDecayMultiplier = 1.0 * (this.m_cmsDecaySpan / this.m_fliv.cms);
    const flDecayPercent = this.m_flDecayPercent;

    const cmsDelta = (this.clockMs() - this.m_msStart) >>> 0;
    let count = Math.trunc(cmsDelta / this.m_cmsDecaySpan + 0.5);
    while (count-- !== 0 && this.m_fHasMagnitude) {
      const dxT = this.m_fliv.dx * flDecayMultiplier;
      const dyT = this.m_fliv.dy * flDecayMultiplier;
      flDecayMultiplier = flDecayMultiplier * (1.0 - flDecayPercent);
      dx += dxT;
      dy += dyT;
      this.CheckMagnitude(dxT, dyT);
    }
    ppt.x = Math.trunc(dx + 0.5);
    ppt.y = Math.trunc(dy + 0.5);

    return true;
  }

  // game/flickscroller.cpp:94-97
  HasMagnitude(): boolean {
    return this.m_fHasMagnitude;
  }

  // game/flickscroller.cpp:99-103
  private CheckMagnitude(dx: number, dy: number): boolean {
    this.m_fHasMagnitude = Math.abs(dx) > 0.1 || Math.abs(dy) > 0.1;
    return this.m_fHasMagnitude;
  }
}
